import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Grid,
  Button,
  Typography,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  LocalizationProvider,
  DatePicker as MuiDatePicker,
} from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useTranslation } from 'react-i18next'
import dateFnsFormat from 'date-fns/format'
import MUIDataTable from 'mui-datatables'

import PageTitle from '../../components/PageTitle'
import Widget from '../../components/Widget'
import InfoPopover from '../../components/info-popover'
import Loading from '../../components/Loading'
import DatePicker from '../../components/DatePicker'
import AutoCompleteInput from '../../components/AutoCompleteInput'
import { useDataApi } from '../../context/DataApiContext'
import { useExport } from '../../context/ExportContext'
import { useNotificationsProvider } from '../../context/NotificationsContext'
import { useQueryState } from '../../utils/query-state'
import { toLocalISOString } from '../../utils/formatters'
import { hasPermission, useUserState } from '../../context/user/UserContext'
import { Permissions } from '../../entities/permissions'
import marketTrackingApi from '../../api/market-tracking'
import useStyles from './styles'

import { ReactComponent as XLSIcon } from '../../assets/Icons_Xls.svg'

const ROW_DATA = 'data'
const ROW_SUBTOTAL = 'subtotal'

function useQuery() {
  const { search } = useLocation()
  return useMemo(() => new URLSearchParams(search), [search])
}

function formatDate(value) {
  if (!value) return ''
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? '' : dateFnsFormat(parsed, 'dd/MM/yyyy')
}

// First and last day of the given month (defaults to the current month), as
// ISO strings the backend accepts.
function monthRange(date) {
  const base = date || new Date()
  const start = new Date(base.getFullYear(), base.getMonth(), 1)
  const end = new Date(base.getFullYear(), base.getMonth() + 1, 0)
  return {
    startDate: toLocalISOString(start),
    endDate: toLocalISOString(end),
  }
}

// For a server-side-paginated page of rows, inject a per-beneficiary subtotal
// header before each group. The count shown is the beneficiary's FULL admitted
// count (from the aggregate), not just this page. The grand total is shown in
// the table toolbar instead of an inline row.
function buildPageDisplayRows(items, subtotals, t) {
  const subtotalByBeneficiary = new Map(
    (subtotals || []).map((s) => [
      s.beneficiaryId != null ? String(s.beneficiaryId) : 'none',
      s,
    ])
  )
  const display = []
  let currentKey = null
  items.forEach((row) => {
    const key = row.beneficiaryId != null ? String(row.beneficiaryId) : 'none'
    if (key !== currentKey) {
      const sub = subtotalByBeneficiary.get(key)
      display.push({
        __type: ROW_SUBTOTAL,
        beneficiary: t('market-tracking.subtotal', {
          name: sub ? sub.beneficiary : row.beneficiary,
        }),
        vehicleTypeAdmittedCount: sub ? sub.count : '',
      })
      currentKey = key
    }
    display.push({ __type: ROW_DATA, ...row })
  })
  return display
}

export default function MarketTracking({ history }) {
  const { t } = useTranslation()
  const classes = useStyles()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const query = useQuery()
  const { dataProvider } = useDataApi()
  const { startExport } = useExport()
  const { showNotification } = useNotificationsProvider()
  const { user } = useUserState()

  // Beneficiaries only ever see their own data, so hide the beneficiary filter
  // for them (they don't have the members page permission anyway).
  const canFilterByBeneficiary = useMemo(
    () => hasPermission(Permissions.PAGE_MEMBERS_ROOT, user),
    [user]
  )

  const [startDate, setStartDate] = useQueryState({
    paramName: 'startDate',
    history,
    query,
    // Default to Jan 1st of the current year so the initial table load isn't
    // the full history (which is slow in production). Cleared via "Effacer".
    fromQuery: useCallback(
      (value) =>
        value ? new Date(value) : new Date(new Date().getFullYear(), 0, 1),
      []
    ),
    toQuery: useCallback((value) => (value ? toLocalISOString(value) : ''), []),
  })
  const [endDate, setEndDate] = useQueryState({
    paramName: 'endDate',
    history,
    query,
    fromQuery: useCallback((value) => (value ? new Date(value) : null), []),
    toQuery: useCallback((value) => (value ? toLocalISOString(value) : ''), []),
  })
  const [beneficiary, setBeneficiary] = useQueryState({
    paramName: 'beneficiary',
    history,
    query,
  })
  const [constructor, setConstructor] = useQueryState({
    paramName: 'constructor',
    history,
    query,
  })
  const [frameworkAgreement, setFrameworkAgreement] = useQueryState({
    paramName: 'frameworkAgreement',
    history,
    query,
  })
  const [purchaseOrder, setPurchaseOrder] = useQueryState({
    paramName: 'purchaseOrder',
    history,
    query,
  })
  const [vehicleType, setVehicleType] = useQueryState({
    paramName: 'vehicleType',
    history,
    query,
  })
  // Off by default: the table then shows only admissions on a marché
  // (contracted), matching the "Total admis" KPI. Turn on to also list legacy
  // admissions with no contract.
  const [includeWithoutContract, setIncludeWithoutContract] = useQueryState({
    paramName: 'withoutContract',
    history,
    query,
    fromQuery: useCallback((value) => value === 'true', []),
    toQuery: useCallback((value) => (value ? 'true' : ''), []),
  })

  const computedFilters = useMemo(() => {
    const filters = {
      startDate: startDate ? toLocalISOString(startDate) : null,
      endDate: endDate ? toLocalISOString(endDate) : null,
      beneficiaryId: beneficiary ? beneficiary.id : null,
      constructorId: constructor ? constructor.id : null,
      frameworkAgreement: frameworkAgreement ? frameworkAgreement.id : null,
      purchaseOrderNumber: purchaseOrder ? purchaseOrder.id : null,
      vehicleTypeId: vehicleType ? vehicleType.id : null,
    }
    Object.keys(filters).forEach(
      (key) => filters[key] === null && delete filters[key]
    )
    if (includeWithoutContract) {
      filters.includeWithoutContract = true
    }
    return filters
  }, [
    startDate,
    endDate,
    beneficiary,
    constructor,
    frameworkAgreement,
    purchaseOrder,
    vehicleType,
    includeWithoutContract,
  ])

  // Filters apply on "Rechercher" (like the other list pages), not on every
  // change, so a fetch only happens when the user commits the filter zone.
  const [appliedFilters, setAppliedFilters] = useState(computedFilters)
  const applyFilters = () => {
    setTablePage(0)
    setAppliedFilters(computedFilters)
  }
  const clearFilters = () => {
    setStartDate(null)
    setEndDate(null)
    setBeneficiary(null)
    setConstructor(null)
    setFrameworkAgreement(null)
    setPurchaseOrder(null)
    setVehicleType(null)
    setIncludeWithoutContract(false)
    setTablePage(0)
    setAppliedFilters({})
  }

  const [loading, setLoading] = useState(true)
  const [displayRows, setDisplayRows] = useState([])
  const [tablePage, setTablePage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  const [itemCount, setItemCount] = useState(0)
  const [grandTotal, setGrandTotal] = useState(0)
  const [kpis, setKpis] = useState({
    totalContracted: 0,
    totalAdmitted: 0,
    executionRate: 0,
  })
  const flattenedRef = useRef([])

  // Rows: server-side paginated (LIMIT/OFFSET). Refetch on filter/page change.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    marketTrackingApi
      .getRows(dataProvider, appliedFilters, {
        page: tablePage + 1,
        limit: rowsPerPage,
      })
      .then((rowsResponse) => {
        if (cancelled) return
        const {
          items = [],
          subtotals = [],
          grandTotal: total = 0,
          itemCount: count = 0,
        } = rowsResponse.data || {}
        const display = buildPageDisplayRows(items, subtotals, t)
        flattenedRef.current = display
        setDisplayRows(display)
        setGrandTotal(total)
        setItemCount(count)
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setLoading(false)
        showNotification('error', t('errors.api-error'))
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, tablePage, rowsPerPage])

  // KPIs: period-independent and page-independent; refetch only on filter change.
  useEffect(() => {
    let cancelled = false
    marketTrackingApi
      .getKpis(dataProvider, appliedFilters)
      .then((kpisResponse) => {
        if (cancelled) return
        setKpis(
          kpisResponse.data || {
            totalContracted: 0,
            totalAdmitted: 0,
            executionRate: 0,
          }
        )
      })
      .catch(() => {
        if (cancelled) return
        showNotification('error', t('errors.api-error'))
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters])

  const executionPct = useMemo(() => {
    const rate = Number(kpis.executionRate) || 0
    return `${(rate * 100).toFixed(1)} %`
  }, [kpis.executionRate])

  const columns = useMemo(() => {
    const rowType = (dataIndex) =>
      (flattenedRef.current[dataIndex] || {}).__type
    const plainOr = (render) => ({
      sort: false,
      customBodyRenderLite: (dataIndex) => {
        if (rowType(dataIndex) === ROW_SUBTOTAL) return ''
        return render(flattenedRef.current[dataIndex] || {})
      },
    })
    return [
      {
        name: 'beneficiary',
        label: t('market-tracking.beneficiary'),
        options: {
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const row = flattenedRef.current[dataIndex] || {}
            const className =
              row.__type === ROW_SUBTOTAL ? classes.subtotalCell : undefined
            return <span className={className}>{row.beneficiary}</span>
          },
        },
      },
      {
        name: 'frameworkAgreement',
        label: t('market-tracking.framework-agreement'),
        options: plainOr((r) => r.frameworkAgreement),
      },
      {
        name: 'purchaseOrderNumber',
        label: t('market-tracking.purchase-order'),
        options: plainOr((r) => r.purchaseOrderNumber),
      },
      {
        name: 'constructor',
        label: t('market-tracking.constructor'),
        options: plainOr((r) => r.constructor),
      },
      {
        name: 'vehicleType',
        label: t('market-tracking.vehicle-type'),
        options: plainOr((r) => r.vehicleType),
      },
      {
        name: 'nParc',
        label: t('market-tracking.parc-number'),
        options: plainOr((r) => r.nParc),
      },
      {
        name: 'admissionDate',
        label: t('market-tracking.admission-date'),
        options: plainOr((r) => formatDate(r.admissionDate)),
      },
      {
        name: 'vehicleTypeAdmittedCount',
        label: t('market-tracking.vehicles-on-type'),
        options: {
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const row = flattenedRef.current[dataIndex] || {}
            const className =
              row.__type === ROW_SUBTOTAL ? classes.subtotalCell : undefined
            return (
              <span className={className}>{row.vehicleTypeAdmittedCount}</span>
            )
          },
        },
      },
    ]
  }, [t, classes])

  const tableOptions = useMemo(
    () => ({
      responsive: 'standard',
      selectableRows: 'none',
      download: false,
      print: false,
      filter: false,
      search: false,
      sort: false,
      viewColumns: false,
      // Server-side pagination: the backend returns one page (LIMIT/OFFSET);
      // count is the full admitted total.
      serverSide: true,
      pagination: true,
      count: itemCount,
      page: tablePage,
      rowsPerPage,
      rowsPerPageOptions: [25, 50, 100],
      onChangePage: (newPage) => setTablePage(newPage),
      onChangeRowsPerPage: (n) => {
        setRowsPerPage(n)
        setTablePage(0)
      },
      // Reuse the shared French table labels so pagination isn't in English.
      textLabels: {
        body: {
          noMatch: t('market-tracking.no-data'),
          toolTip: t('tables.body.toolTip'),
        },
        pagination: {
          next: t('tables.pagination.next'),
          previous: t('tables.pagination.previous'),
          rowsPerPage: t('tables.pagination.rowsPerPage'),
          displayRows: t('tables.pagination.displayRows'),
        },
      },
    }),
    [t, itemCount, tablePage, rowsPerPage]
  )

  // Exports go through the async pipeline (authenticated POST → S3 presigned
  // download) so an expired session is refreshed instead of failing in a new
  // tab with a stale token in the URL.
  const runExport = (asyncType, filters) => {
    startExport(asyncType, { filters }).catch(() => {
      // startExport already surfaces an error notification.
    })
  }

  // View export: everything matching the active filters (no dates = all).
  const [viewMenuAnchor, setViewMenuAnchor] = useState(null)
  const openViewMenu = (event) => setViewMenuAnchor(event.currentTarget)
  const closeViewMenu = () => setViewMenuAnchor(null)
  const doViewExport = (fileType) => {
    closeViewMenu()
    runExport(`market-tracking-${fileType}`, appliedFilters)
  }

  // Monthly billing export: pick the month (defaults to the current one). The
  // chosen month overrides any table period; other filters (beneficiary /
  // manufacturer) still apply.
  const [monthlyDialog, setMonthlyDialog] = useState({
    open: false,
    month: null,
  })
  const openMonthlyDialog = () =>
    setMonthlyDialog({ open: true, month: new Date() })
  const closeMonthlyDialog = () =>
    setMonthlyDialog((state) => ({ ...state, open: false }))
  const doMonthlyExport = (fileType) => {
    const filters = { ...appliedFilters, ...monthRange(monthlyDialog.month) }
    closeMonthlyDialog()
    runExport(`market-tracking-monthly-${fileType}`, filters)
  }

  // A widget title with an inline (i) info popover explaining the KPI.
  const kpiTitle = (labelKey, infoKey) => (
    <span className={classes.titleWithInfo}>
      {t(`market-tracking.${labelKey}`)}
      <InfoPopover message={t(`market-tracking.${infoKey}`)} />
    </span>
  )

  return (
    <Grid container direction="column">
      <Grid
        item
        container
        justifyContent="space-between"
        alignItems="center"
        className={classes.headerRow}
        direction={isSmall ? 'column' : 'row'}
      >
        <PageTitle title={t('market-tracking.title')} />
        <Grid item>
          <Button
            variant="outlined"
            className={classes.exportButton}
            startIcon={<XLSIcon className={classes.exportIcon} />}
            onClick={openViewMenu}
          >
            {t('market-tracking.export-view')}
          </Button>
          <Button
            variant="outlined"
            className={classes.exportButton}
            startIcon={<XLSIcon className={classes.exportIcon} />}
            onClick={openMonthlyDialog}
          >
            {t('market-tracking.export-monthly')}
          </Button>
          <Menu
            anchorEl={viewMenuAnchor}
            open={Boolean(viewMenuAnchor)}
            onClose={closeViewMenu}
          >
            <MenuItem onClick={() => doViewExport('csv')}>
              {t('market-tracking.export-csv')}
            </MenuItem>
            <MenuItem onClick={() => doViewExport('xlsx')}>
              {t('market-tracking.export-excel')}
            </MenuItem>
          </Menu>
          <Dialog
            open={monthlyDialog.open}
            onClose={closeMonthlyDialog}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>
              {t('market-tracking.export-monthly-title')}
            </DialogTitle>
            <DialogContent>
              <Grid container direction="column" spacing={2} sx={{ pt: 1 }}>
                <Grid item>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <MuiDatePicker
                      views={['year', 'month']}
                      openTo="month"
                      label={t('market-tracking.month')}
                      value={monthlyDialog.month}
                      onChange={(month) =>
                        setMonthlyDialog((state) => ({ ...state, month }))
                      }
                      inputFormat="MM/yyyy"
                      disableMaskedInput
                      renderInput={(params) => (
                        <TextField {...params} fullWidth variant="standard" />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item container spacing={2}>
                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="secondary"
                      sx={{ height: '100%' }}
                      onClick={closeMonthlyDialog}
                    >
                      {t('common.cancel')}
                    </Button>
                  </Grid>
                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      sx={{ height: '100%' }}
                      startIcon={<XLSIcon />}
                      onClick={() => doMonthlyExport('csv')}
                      disabled={!monthlyDialog.month}
                    >
                      {t('market-tracking.export-csv')}
                    </Button>
                  </Grid>
                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      sx={{ height: '100%' }}
                      startIcon={<XLSIcon />}
                      onClick={() => doMonthlyExport('xlsx')}
                      disabled={!monthlyDialog.month}
                    >
                      {t('market-tracking.export-excel')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </DialogContent>
          </Dialog>
        </Grid>
      </Grid>

      {/* KPIs */}
      <Grid item container spacing={2} className={classes.headerRow}>
        <Grid item xs={12} sm={4}>
          <Widget
            title={kpiTitle('kpi-contracted', 'kpi-contracted-info')}
            disableWidgetMenu
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <Typography className={classes.kpiValue}>
                {kpis.totalContracted}
              </Typography>
            )}
          </Widget>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Widget
            title={kpiTitle('kpi-admitted', 'kpi-admitted-info')}
            disableWidgetMenu
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <Typography className={classes.kpiValue}>
                {kpis.totalAdmitted}
              </Typography>
            )}
          </Widget>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Widget
            title={kpiTitle('kpi-execution', 'kpi-execution-info')}
            disableWidgetMenu
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <Typography className={classes.kpiValue}>
                {executionPct}
              </Typography>
            )}
          </Widget>
        </Grid>
      </Grid>

      {/* Filters */}
      <Grid item className={classes.filtersContainer}>
        <Grid container spacing={4} direction="column">
          <Grid item>
            <Typography
              variant="h6"
              className={classes.filterContainerTitleText}
            >
              {t('common.filterZone')}
            </Typography>
          </Grid>
          <Grid item>
            <Grid
              container
              justifyContent="flex-start"
              spacing={1}
              wrap="wrap"
              direction="row"
            >
              <Grid item xs={12} md={3}>
                <DatePicker
                  id="startDate"
                  label={t('market-tracking.period-start')}
                  value={startDate}
                  onChange={setStartDate}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  id="endDate"
                  label={t('market-tracking.period-end')}
                  value={endDate}
                  onChange={setEndDate}
                />
              </Grid>
              {canFilterByBeneficiary && (
                <Grid item xs={12} md={3}>
                  <AutoCompleteInput
                    label={t('market-tracking.beneficiary')}
                    resource="members/autocomplete"
                    value={beneficiary}
                    onChange={setBeneficiary}
                    canDelete
                  />
                </Grid>
              )}
              <Grid item xs={12} md={3}>
                <AutoCompleteInput
                  label={t('market-tracking.constructor')}
                  resource="constructors/autocomplete"
                  value={constructor}
                  onChange={setConstructor}
                  canDelete
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <AutoCompleteInput
                  label={t('market-tracking.filter-framework-agreement')}
                  resource="market-tracking/framework-agreements/autocomplete"
                  value={frameworkAgreement}
                  onChange={setFrameworkAgreement}
                  canDelete
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <AutoCompleteInput
                  label={t('market-tracking.filter-purchase-order')}
                  resource="market-tracking/purchase-orders/autocomplete"
                  value={purchaseOrder}
                  onChange={setPurchaseOrder}
                  canDelete
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <AutoCompleteInput
                  label={t('market-tracking.vehicle-type')}
                  resource="market-tracking/vehicle-types/autocomplete"
                  value={vehicleType}
                  onChange={setVehicleType}
                  canDelete
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!includeWithoutContract}
                      onChange={(e) =>
                        setIncludeWithoutContract(e.target.checked)
                      }
                    />
                  }
                  label={t('market-tracking.include-without-contract')}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid
              container
              spacing={2}
              justifyContent="flex-end"
              className={classes.actionsContainer}
            >
              <Grid item xs={12} md={2} xl={1}>
                <Button
                  color="secondary"
                  variant="contained"
                  fullWidth
                  onClick={clearFilters}
                >
                  {t('common.clean')}
                </Button>
              </Grid>
              <Grid item xs={12} md={2} xl={1}>
                <Button
                  color="primary"
                  variant="contained"
                  fullWidth
                  onClick={applyFilters}
                >
                  {t('common.search')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Table */}
      <Grid item className={classes.tableContainer}>
        {loading ? (
          <Loading minHeight="240px" />
        ) : (
          <MUIDataTable
            title={
              <span className={classes.titleWithInfo}>
                <Typography className={classes.tableTitle}>
                  {`${t('market-tracking.grand-total')}: ${grandTotal}`}
                </Typography>
                <InfoPopover message={t('market-tracking.grand-total-info')} />
              </span>
            }
            data={displayRows}
            columns={columns}
            options={tableOptions}
            className={classes.table}
          />
        )}
      </Grid>
    </Grid>
  )
}
